/* Server-authoritative sorting and filtering with FLIP motion. Native GET remains the fallback.
 * A sort option is a submit button: its click is handled directly, so the choice
 * never depends on SubmitEvent.submitter (Safari differs there); the submit
 * listener covers form.requestSubmit(). Every other submit (Apply, Enter in a
 * filter field) and the chip, Clear all and empty-state links apply filters in
 * place: the drawer closes, the page glides back to the toolbar only if it was
 * scrolled past, and the dimmed grid is replaced under a new history entry.
 * Any failure is logged and falls back to a plain navigation to that URL.
 * Keys include occurrence so repeated cards in the comparison template are unique.
 * Load more appends the next page's cards in place (without JS the link opens
 * that page). The first one runs by itself a screen before the shopper reaches
 * it; later ones wait for a click, so the footer stays within reach. While a
 * next page exists the grid ends on a complete row: the cells of an unfinished
 * last row are hidden and lead the next page. Rows are read from the rendered
 * grid, so any column count, promo tile or span works, and the count shows only
 * what is on screen. Whenever the next page is left to the browser (a click whose
 * request failed, a modified or middle click, the link's context menu) the
 * held-back cells return first, so that page starts right after them. A next page
 * that comes back empty (the collection shrank meanwhile) ends the list there, with
 * every cell shown. The number of appended pages and the scroll position live in
 * history.state, written whenever the shopper leaves an entry (for a product, a
 * sort or a filter), so coming back rebuilds the same list and scrolls to where
 * the shopper was (a bfcache restore keeps the DOM and needs nothing). */
if (!customElements.get('collection-products')) {
  // Block-scoped, so a second evaluation of this file (theme editor) cannot redeclare them.
  const isPlainClick = (event) => !event.defaultPrevented && event.button === 0
    && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
  const AUTO_PAGES = 1;

  customElements.define('collection-products', class extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.addEventListener('click', this.onSortClick, options);
      ['click', 'auxclick', 'contextmenu'].forEach((type) => this.addEventListener(type, this.onMoreClick, options));
      this.addEventListener('click', this.onFilterLinkClick, options);
      this.addEventListener('submit', this.onSubmit, options);
      window.addEventListener('popstate', this.onPopState, options);
      window.addEventListener('pagehide', () => this.remember(), options);
      // Columns and spans change with the width alone. Refilling in the next frame
      // keeps the grid's new height out of this observer's own loop.
      this.resize = new ResizeObserver(([entry]) => {
        if (entry.contentRect.width === this.lastWidth) return;
        this.lastWidth = entry.contentRect.width;
        requestAnimationFrame(() => this.fill());
      });
      this.resize.observe(this);
      this.appended = 0;
      this.fill();
      this.restore();
    }

    disconnectedCallback() {
      this.events.abort();
      this.request?.abort();
      this.resize.disconnect();
      this.autoLoad?.disconnect();
    }

    onPopState = async () => {
      if (await this.load(new URL(location.href), false)) this.restore();
    };

    // A plain click appends the next page here. Any other way into the link (a modified or
    // middle click, its context menu) is left to the browser, which opens that page as the
    // server paginates it, so the link is released first: the held-back cells return here.
    onMoreClick = (event) => {
      const link = event.target.closest('[data-collection-more] a');
      if (!link || event.defaultPrevented) return;
      if (event.type !== 'click' || !isPlainClick(event)) {
        this.release();
        return;
      }
      event.preventDefault();
      if (!this.hasAttribute('aria-busy')) this.more(new URL(link.href));
    };

    // Chips, Clear all and the empty-state link already point at the filtered URL.
    onFilterLinkClick = (event) => {
      const link = event.target.closest('[data-filters-panel] a[href], [data-collection-empty] a[href]');
      if (!link || !isPlainClick(event)) return;
      event.preventDefault();
      this.filter(new URL(link.href));
    };

    // Records this entry for coming back to it; called whenever the shopper leaves it.
    remember() {
      history.replaceState({ ...history.state, collectionAppended: this.appended, collectionScroll: scrollY }, '');
    }

    // Filter Apply and the hidden default button also submit sort_by (to keep
    // the current sort), so only dropdown options count as a sort choice.
    onSortClick = (event) => {
      const button = event.target.closest('button[name="sort_by"][data-dropdown-option]');
      if (!button || event.defaultPrevented) return;
      event.preventDefault();
      this.sort(button);
    };

    onSubmit = (event) => {
      if (event.defaultPrevented || !event.target.matches('.main-collection-products__form')) return;
      event.preventDefault();
      if (event.submitter?.matches('[data-dropdown-option]')) this.sort(event.submitter);
      else this.filter(this.filterUrl(event.target));
    };

    // The drawer's checked values; the sort comes from the URL, as in sort().
    filterUrl(form) {
      const url = new URL(form.action);
      new FormData(form).forEach((value, name) => {
        if (value !== '') url.searchParams.append(name, value);
      });
      const sort = new URL(location.href).searchParams.get('sort_by');
      if (sort) url.searchParams.set('sort_by', sort);
      return url;
    }

    // Focus returns to whichever Filters trigger is rendered: the drawer's button, or the
    // link that stands in for it until <collection-toolbar> is defined.
    filter(url) {
      const toolbar = this.querySelector('collection-toolbar');
      const ready = () => Promise.resolve(toolbar?.closeFilters?.()).then(() => this.revealResults());
      this.load(url, true, { ready, focus: '[data-filters-toggle], [data-filters-link]' });
    }

    // Results start at the toolbar. A shopper scrolled past it glides back up;
    // anywhere else the page stays put. Resolves when the glide has landed,
    // or after a second if the shopper scrolls elsewhere meanwhile.
    revealResults() {
      const start = this.querySelector('.main-collection-products__form');
      if (!start) return;
      const offset = start.getBoundingClientRect().top - parseFloat(getComputedStyle(start).scrollMarginTop);
      if (offset >= 0) return;
      const top = Math.max(0, scrollY + offset);
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
        scrollTo(0, top);
        return;
      }
      scrollTo({ top, behavior: 'smooth' });
      // The timer, not a frame, bounds the wait: a background tab runs no frames.
      return new Promise((resolve) => {
        let frame;
        const done = () => {
          cancelAnimationFrame(frame);
          clearTimeout(timer);
          resolve();
        };
        const timer = setTimeout(done, 1000);
        const check = () => {
          if (Math.abs(scrollY - top) < 1) done();
          else frame = requestAnimationFrame(check);
        };
        frame = requestAnimationFrame(check);
      });
    }

    sort(button) {
      // Preserve applied filters from the URL, not unsubmitted drawer edits.
      const url = new URL(location.href);
      url.searchParams.set('sort_by', button.value);
      url.searchParams.delete('page');
      const dropdown = button.closest('asa-dropdown');
      dropdown?.toggle(false);
      dropdown?.querySelector('[data-dropdown-toggle]')?.focus({ preventScroll: true });
      this.load(url, true);
    }

    entries(grid) {
      const counts = new Map();
      return Array.from(grid?.children || [], (element) => {
        const id = element.dataset.productId || 'custom-piece';
        const occurrence = counts.get(id) || 0;
        counts.set(id, occurrence + 1);
        return { key: `${id}:${occurrence}`, element };
      });
    }

    // This section rendered for `url`, through the Section Rendering API.
    async fetchSection(url, signal) {
      const endpoint = new URL(url);
      endpoint.searchParams.set('section_id', this.dataset.section);
      const response = await fetch(endpoint, { signal });
      if (!response.ok) throw new Error(`Collection response ${response.status}`);
      const html = await response.text();
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('collection-products');
      if (!fresh || fresh.dataset.section !== this.dataset.section) throw new Error('Missing collection section');
      // Links rendered for a section request may carry section_id; followed as-is they would open the bare section.
      fresh.querySelectorAll('a[href*="section_id="]').forEach((link) => {
        const href = new URL(link.href, location.href);
        href.searchParams.delete('section_id');
        link.setAttribute('href', href.pathname + href.search + href.hash);
      });
      return fresh;
    }

    // Resolves true once the section shows `url`; on failure it navigates there instead.
    // Before a new history entry, the current one records its pages and scroll, while the
    // page is still where the shopper left it: `ready` (the drawer closing, the glide back
    // up) starts only then, and the new markup waits for it. Focus that was inside the
    // section when the request started moves to the first rendered match of `focus` in the
    // new markup: a closed modal may have already dropped it on <body> by then.
    async load(url, pushHistory, { ready, focus = '[data-dropdown-toggle]' } = {}) {
      this.request?.abort();
      const request = this.request = new AbortController();
      const refocus = this.contains(document.activeElement) ? focus : null;
      if (pushHistory) this.remember();
      this.setAttribute('aria-busy', 'true');
      this.toggleAttribute('data-loading', true);
      try {
        const [fresh] = await Promise.all([this.fetchSection(url, request.signal), ready?.()]);
        if (request.signal.aborted) return false;
        this.render(fresh, refocus);
        this.appended = 0;
        if (pushHistory) history.pushState(null, '', url.href);
        this.watch();
        return true;
      } catch (error) {
        if (request.signal.aborted) return false;
        console.warn('[collection-products] falling back to navigation:', error);
        location.assign(url.href);
        return false;
      } finally {
        if (this.request === request) {
          this.removeAttribute('aria-busy');
          this.removeAttribute('data-loading');
        }
      }
    }

    // Appends the page behind the Load more link after the cells held back for it.
    // `source` is click, auto or restore. A click moves focus to the first arriving
    // card, and its failure releases the link. An automatic load or a restore stays
    // quiet on failure and leaves the link in place, so the next click tries again.
    // A restore also skips the fade. Past the last page Shopify renders the empty
    // state: any source then ends the list (held cells shown, Load more removed);
    // only a click moves focus. A response with neither cards nor the empty state
    // counts as a failure.
    async more(url, source = 'click') {
      this.request?.abort();
      const request = this.request = new AbortController();
      this.setAttribute('aria-busy', 'true');
      try {
        const fresh = await this.fetchSection(url, request.signal);
        const current = this.querySelector('[data-collection-more]');
        // Superseded, or released meanwhile: the browser now owns the link.
        if (request.signal.aborted || !current) return false;
        const cards = Array.from(fresh.querySelector('[data-collection-grid]')?.children || []);
        if (!cards.length) {
          if (!fresh.querySelector('[data-collection-empty]')) throw new Error('Next page has no cards');
          this.release({ end: true, focus: source === 'click' });
          return false;
        }
        const grid = this.querySelector('[data-collection-grid]');
        const arriving = [...grid.querySelectorAll(':scope > [hidden]'), ...cards];
        grid.append(...cards);
        // Only where the loaded pages end moves; the count's template, and so its start, stays.
        const loadedTo = fresh.querySelector('[data-collection-shown]')?.dataset.loadedTo;
        const status = this.querySelector('[data-collection-shown]');
        if (loadedTo && status) status.dataset.loadedTo = loadedTo;
        const next = fresh.querySelector('[data-collection-more]');
        if (next) current.replaceWith(next);
        else current.remove();
        this.fill();
        this.appended += 1;
        if (source !== 'restore') {
          history.replaceState({ ...history.state, collectionAppended: this.appended }, '');
          this.fadeIn(arriving.filter((cell) => !cell.hidden));
          this.watch();
        }
        if (source === 'click') arriving.find((cell) => !cell.hidden)?.querySelector('a[href]')?.focus({ preventScroll: true });
        return true;
      } catch (error) {
        if (request.signal.aborted || source !== 'click') return false;
        console.warn('[collection-products] Load more failed; its link now opens the next page:', error);
        this.release({ focus: true });
        return false;
      } finally {
        if (this.request === request) this.removeAttribute('aria-busy');
      }
    }

    // Stops Load more; the page stays. Without the hook nothing holds cells back for the
    // link, intercepts it or loads it automatically: the held-back cells return and count
    // again. The link stays and opens the next page, which starts right after them
    // (navigating at once would skip them), unless `end` removes it: nothing follows.
    // `focus` moves focus to the first returned cell; a removed link also passes on its
    // own focus, to the last cell when none was held back.
    release({ end = false, focus = false } = {}) {
      const more = this.querySelector('[data-collection-more]');
      if (!more) return;
      const held = Array.from(this.querySelectorAll('[data-collection-grid] > [hidden]'));
      const refocus = focus || (end && more.contains(document.activeElement));
      if (end) more.remove();
      else more.removeAttribute('data-collection-more');
      this.autoLoad?.disconnect();
      this.fill();
      this.fadeIn(held);
      if (!refocus) return;
      const target = held[0] || (end ? this.querySelector('[data-collection-grid] > :last-child') : null);
      target?.querySelector('a[href]')?.focus({ preventScroll: true });
    }

    // Rebuilds the pages appended before the shopper left this entry, then scrolls back.
    // The scroll applies with nothing appended too: after a sort or filter the browser's
    // own restoration ran against the other list, and after a glide it saved the wrong spot.
    async restore() {
      const { collectionAppended: pages = 0, collectionScroll: top } = history.state || {};
      for (let page = this.appended; page < pages; page += 1) {
        const link = this.querySelector('[data-collection-more] a');
        if (!link || !(await this.more(new URL(link.href), 'restore'))) break;
      }
      if (typeof top === 'number') window.scrollTo(0, top);
      this.watch();
    }

    // The first Load more runs by itself once the link is a screen away; the observer
    // skips while another request runs, and every path that ends one calls watch() again.
    watch() {
      this.autoLoad?.disconnect();
      const link = this.querySelector('[data-collection-more] a');
      if (!link || this.appended >= AUTO_PAGES) return;
      this.autoLoad = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting || this.hasAttribute('aria-busy')) return;
        this.autoLoad.disconnect();
        this.more(new URL(link.href), 'auto');
      }, { rootMargin: '0px 0px 100% 0px' });
      this.autoLoad.observe(link);
    }

    // While Load more can append a next page, hides the cells of an unfinished last row;
    // they lead that page once it arrives. The row is read from layout (offsets ignore
    // hover and FLIP transforms): cells that start at the lowest top, each spanning its
    // width in tracks.
    fill() {
      const grid = this.querySelector('[data-collection-grid]');
      if (!grid) return;
      grid.querySelectorAll(':scope > [hidden]').forEach((cell) => { cell.hidden = false; });
      const cells = Array.from(grid.children);
      if (cells.length && this.querySelector('[data-collection-more]')) {
        const style = getComputedStyle(grid);
        const columns = style.gridTemplateColumns.split(' ');
        const gap = parseFloat(style.columnGap) || 0;
        const track = parseFloat(columns[0]) + gap;
        const top = Math.max(...cells.map((cell) => cell.offsetTop));
        const row = cells.filter((cell) => top - cell.offsetTop < 2);
        const used = row.reduce((sum, cell) => sum + Math.round((cell.offsetWidth + gap) / track), 0);
        if (used < columns.length && row.length < cells.length) row.forEach((cell) => { cell.hidden = true; });
      }
      this.recount();
    }

    // The count and the progress line end at the last card on screen: where the loaded
    // pages end, less the cards held back. A list from the first product counts them
    // ("Shown 24 of 60"); one opened on a later page names its range ("Shown 25–48 of 60"),
    // and appending never moves its start. The line fills to that last position, so it
    // reaches the end together with the list.
    recount() {
      const status = this.querySelector('[data-collection-shown]');
      const { loadedTo, template } = status?.dataset || {};
      if (!loadedTo || !template) return;
      const to = Number(loadedTo) - this.querySelectorAll('[data-collection-grid] > [hidden][data-product-id]').length;
      const text = template.replace('[to]', to);
      if (status.textContent !== text) status.textContent = text;
      const progress = this.querySelector('[data-collection-progress]');
      if (progress) progress.style.setProperty('--main-collection-products-shown', to / Number(progress.dataset.total));
    }

    fadeIn(elements) {
      if (!elements.length || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const easing = getComputedStyle(this).getPropertyValue('--asa-ease').trim() || 'ease';
      elements.forEach((element) => element.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 480, easing }));
    }

    render(fresh, focus) {
      const selector = '[data-collection-grid]';
      // Held-back cells have no position to move from; they count as new.
      const previous = new Map(this.entries(this.querySelector(selector)).filter(({ element }) => !element.hidden)
        .map(({ key, element }) => [key, { element, rect: element.getBoundingClientRect() }]));
      // Capture visible positions before cancelling an earlier interrupted move.
      previous.forEach(({ element }) => element.getAnimations().forEach((animation) => animation.cancel()));
      const grid = fresh.querySelector(selector);
      const entries = this.entries(grid);
      entries.forEach((entry) => {
        const old = previous.get(entry.key)?.element;
        if (old && old.outerHTML === entry.element.outerHTML) {
          entry.element.replaceWith(old);
          entry.element = old;
        }
      });
      // Unless the shopper has since focused something else on the page.
      const restoreFocus = focus && (this.contains(document.activeElement) || document.activeElement === document.body);
      this.replaceChildren(...fresh.childNodes);
      this.fill();
      if (restoreFocus) {
        const target = Array.from(this.querySelectorAll(focus)).find((element) => element.getClientRects().length);
        target?.focus({ preventScroll: true });
      }
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const easing = getComputedStyle(this).getPropertyValue('--asa-ease').trim() || 'ease';
      const arriving = [];
      entries.forEach(({ key, element }) => {
        if (element.hidden) return;
        const before = previous.get(key)?.rect;
        if (!before) {
          arriving.push(element);
          return;
        }
        const after = element.getBoundingClientRect();
        const x = before.left - after.left;
        const y = before.top - after.top;
        if (Math.abs(x) + Math.abs(y) < 1) return;
        element.animate([
          { transform: `translate(${x}px, ${y}px)` },
          { transform: 'translate(0, 0)' },
        ], { duration: 680, easing });
      });
      this.fadeIn(arriving);
    }
  });
}
